import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchBrands } from "../../services/brandApi";
import {
  createSeries,
  fetchCategories,
  fetchSeries,
  updateSeries,
} from "../../services/deviceApi";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const emptyForm = {
  name: "",
  displayOrder: "0",
  isActive: true,
};

const Series = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [seriesList, setSeriesList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchBrands().then(setBrands).catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  const loadSeries = async () => {
    if (!brandFilter || !categoryFilter) {
      setSeriesList([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      setSeriesList(await fetchSeries(brandFilter, categoryFilter));
    } catch (err) {
      setError(err.message);
      setSeriesList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandFilter, categoryFilter]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brandFilter || !categoryFilter) {
      setError("Select brand and category first");
      return;
    }
    setSuccess("");
    setError("");
    const payload = {
      name: form.name.trim(),
      brandId: parseInt(brandFilter, 10),
      categoryId: parseInt(categoryFilter, 10),
      displayOrder: parseInt(form.displayOrder, 10) || 0,
      isActive: form.isActive,
    };
    try {
      if (editingId) {
        await updateSeries(editingId, payload);
        setSuccess("Series updated.");
      } else {
        await createSeries(payload);
        setSuccess("Series created.");
      }
      resetForm();
      await loadSeries();
    } catch (err) {
      setError(err.message || "Failed to save series");
    }
  };

  const startEdit = (series) => {
    setEditingId(series.id);
    setForm({
      name: series.name,
      displayOrder: series.displayOrder != null ? String(series.displayOrder) : "0",
      isActive: series.isActive !== false,
    });
  };

  const brandName = (id) => brands.find((b) => String(b.id) === String(id))?.name || "—";
  const categoryName = (id) => categories.find((c) => String(c.id) === String(id))?.name || "—";

  return (
    <div className="admin-page">
      <h2>Device series</h2>
      <p className="subtitle">
        Group models into series for the sell flow (e.g. Galaxy A, Galaxy S). Assign models to a series on the{" "}
        <Link to="/models">Models</Link> page.
      </p>
      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-filters">
        <label className="field-label">
          Brand
          <select
            value={brandFilter}
            onChange={(e) => {
              setBrandFilter(e.target.value);
              resetForm();
            }}
          >
            <option value="">Select brand</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
        <label className="field-label">
          Product category
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              resetForm();
            }}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      {brandFilter && categoryFilter && (
        <div className="admin-split">
          <div className="admin-card">
            <h3>{editingId ? `Edit series #${editingId}` : "Add series"}</h3>
            <p className="field-hint">
              {brandName(brandFilter)} · {categoryName(categoryFilter)}
            </p>
            <form className="admin-form" onSubmit={handleSubmit}>
              <label className="field-label">
                Series name
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Galaxy A Series"
                  required
                />
              </label>
              <label className="field-label">
                Display order
                <input
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                />
              </label>
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active
              </label>
              <div className="form-actions">
                <button type="submit" className="admin-btn">
                  {editingId ? "Update" : "Create"}
                </button>
                {editingId && (
                  <button type="button" className="admin-btn secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="admin-card">
            <h3>Series list</h3>
            {loading ? (
              <div className="admin-loading">Loading…</div>
            ) : (
              <TableScroll>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Models</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {seriesList.length === 0 && (
                      <tr><td colSpan={5} className="empty-msg">No series yet</td></tr>
                    )}
                    {seriesList.map((s) => (
                      <tr key={s.id}>
                        <td><strong>{s.name}</strong></td>
                        <td>{s.deviceCount ?? 0}</td>
                        <td>{s.displayOrder ?? 0}</td>
                        <td className={s.isActive !== false ? "badge-active" : "badge-inactive"}>
                          {s.isActive !== false ? "Active" : "Inactive"}
                        </td>
                        <td className="actions-cell">
                          <button type="button" className="admin-btn secondary" onClick={() => startEdit(s)}>
                            Edit
                          </button>
                          <Link
                            className="admin-btn"
                            to={`/models?brandId=${brandFilter}&categoryId=${categoryFilter}`}
                          >
                            Models
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Series;
